import React from "react";
import { Radio, Brain, Network, Building2, TrendingUp } from "lucide-react";
import StarField from "./StarField";

/**
 * AboutHowItWorks v2 — premium dark-navy stage cards.
 *
 *  Replaces the previous light-card grid with a navy band that visually
 *  matches the hero, /buy-leads, and the footer. Numbered stage cards stack
 *  horizontally with a tracked connecting line.
 */
const STAGES = [
  { n: "01", Icon: Radio,      title: "Consumer demand",   body: "Real customer enquiries land via our publisher network, AI search and partner sites — all consent-captured with TrustedForm." },
  { n: "02", Icon: Brain,      title: "AI qualification",  body: "Voice and chat agents qualify, FCA-script and affordability-check every lead before it touches a buyer's CRM." },
  { n: "03", Icon: Network,    title: "Lead distribution", body: "Our routing engine matches each qualified lead to its single exclusive buyer in under 100ms at a fixed price per sector — 100% exclusive, never resold." },
  { n: "04", Icon: Building2,  title: "Business connection", body: "Delivered to the buyer's CRM in real time via webhook, API, live phone transfer or branded portal — buyer ready." },
  { n: "05", Icon: TrendingUp, title: "Revenue growth",    body: "Buyers settle from prepaid wallet on accepted leads. Publishers earn 90% rev-share. Connexis takes its operating margin in between." },
];

export default function AboutHowItWorks() {
  return (
    <section className="relative bg-[#0F172A] text-white py-24 lg:py-32 overflow-hidden" data-testid="about-how-it-works">
      <StarField opacity={0.4} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7da9ff]">
            How the network operates
          </p>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-[56px] tracking-[-0.035em] font-semibold leading-[1.02]">
            Five stages. One continuous flow.
          </h2>
          <p className="mt-5 text-[16.5px] text-white/60 leading-relaxed max-w-2xl">
            From the first click of a consumer to the buyer's revenue line, every step runs through our infrastructure. Sub-second routing. Single source of truth. No black box.
          </p>
        </div>

        <div className="mt-16 relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 relative">
            {STAGES.map((s, i) => (
              <div key={s.n} className="relative" data-testid={`stage-${i}`}>
                {/* Stage card */}
                <div className="relative h-full rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 hover:border-[#3d7bff]/45 hover:bg-white/[0.05] hover:-translate-y-1 transition-all duration-300 group">
                  {/* Soft top-glow accent on hover */}
                  <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#3d7bff]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="flex items-center justify-between">
                    <span className="inline-grid place-items-center size-11 rounded-xl bg-[#3d7bff]/[0.12] text-[#7da9ff] ring-1 ring-[#3d7bff]/30">
                      <s.Icon className="size-[22px]" strokeWidth={1.7} />
                    </span>
                    <span className="text-[28px] font-semibold tracking-[-0.04em] text-white/25 tabular-nums">{s.n}</span>
                  </div>

                  <h3 className="mt-7 text-[17px] tracking-tight font-semibold text-white leading-tight">{s.title}</h3>
                  <p className="mt-2 text-[12.5px] text-white/55 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* KPI strip with subtle separator */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 pt-12 border-t border-white/10">
          {[
            { v: "240,000+", l: "Enquiries routed / month" },
            { v: "98.6ms",   l: "Median routing latency" },
            { v: "99.99%",   l: "Routing uptime" },
            { v: "10",       l: "Financial sectors operated" },
          ].map((m, i) => (
            <div key={i}>
              <div className="text-4xl sm:text-5xl tracking-[-0.025em] font-semibold text-white tabular-nums">{m.v}</div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.18em] text-white/45 font-medium">{m.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
